/* eslint-disable no-nested-ternary */
/* eslint-disable radix */
/* eslint-disable no-plusplus */
import { ReactElement, useEffect, useMemo, useState } from 'react';

import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
  ReferenceLine,
} from 'recharts';

import { useLoading } from '~/hooks/loading';
import useQuery from '~/hooks/useQuery';
import {
  IAnnualProfessionalByPaymentStatusWithMonth,
  IAnnualProfessionalByStatusWithMonth,
  IHistoricAnnualProfessionalByStatus,
  IProfessionalByAccessControl,
  IProfessionalsData,
} from '~/models/Boss';
import ExplainDate from '~/pages/Billing/ExplainDate';
import { IBossService } from '~/services';
import { handleGetAgeGroup } from '~/utils/financeReports';

import * as S from './styles';

const months = [
  '',
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

const dataTest = [
  { name: 'Jan 2020', sales: 1000, expenses: 400 },
  { name: 'Fev 2020', sales: 1200, expenses: 500 },
  { name: 'Mar 2020', sales: 1400, expenses: 600 },
  { name: 'Abr 2020', sales: 1600, expenses: 700 },
  { name: 'Mai 2020', sales: 1800, expenses: 800 },
  { name: 'Jun 2020', sales: 2000, expenses: 900 },
  { name: 'Jul 2020', sales: 2200, expenses: 1000 },
  { name: 'Ago 2020', sales: 2400, expenses: 1100 },
  { name: 'Set 2020', sales: 2600, expenses: 1200 },
  { name: 'Out 2020', sales: 2800, expenses: 1300 },
  { name: 'Nov 2020', sales: 3000, expenses: 1400 },
  { name: 'Dez 2020', sales: 3200, expenses: 1500 },
  { name: 'Jan 2021', sales: 3400, expenses: 1600 },
  { name: 'Fev 2021', sales: 3600, expenses: 1700 },
  { name: 'Mar 2021', sales: 3800, expenses: 1800 },
  { name: 'Abr 2021', sales: 4000, expenses: 1900 },
  { name: 'Mai 2021', sales: 4200, expenses: 2000 },
  { name: 'Jun 2021', sales: 4400, expenses: 2100 },
  { name: 'Jul 2021', sales: 4600, expenses: 2200 },
  { name: 'Ago 2021', sales: 4800, expenses: 2300 },
  { name: 'Set 2021', sales: 5000, expenses: 2400 },
  { name: 'Out 2021', sales: 5200, expenses: 2500 },
  { name: 'Nov 2021', sales: 5400, expenses: 2600 },
  { name: 'Dez 2021', sales: 5600, expenses: 2700 },
];

function TrainersCharts(): ReactElement {
  const { setLoading } = useLoading();
  const query = useQuery();
  const [trainersData, setTrainerData] = useState<IProfessionalsData>();
  const [
    annualProfessionalByPaymentStatusWithMonth,
    setAnnualProfessionalByPaymentStatusWithMonth,
  ] = useState<
    {
      month: number;
      monthName: string;
      nullCount: number;
      pendingCount: number;
      overdueCount: number;
      paidCount: number;
      canceledCount: number;
      year?: string;
    }[]
  >([]);

  const [
    historicAnnualProfessionalByStatus,
    setHistoricAnnualProfessionalByStatus,
  ] = useState<
    {
      year: number;
      active_count: string;
      inactive_count: string;
    }[]
  >([]);

  const [
    professionalByAccessControl,
    setProfessionalByAccessControl,
  ] = useState<
    {
      access_control: string;
      count: number;
    }[]
  >([]);

  const [
    annualProfessionalByStatusWithMonth,
    setAnnualProfessionalByStatusWithMonth,
  ] = useState<
    {
      month: number;
      monthName: string;
      active_number: number;
      inactive_number: number;
    }[]
  >([]);

  const [
    annualProfessionalByStatusWithAge,
    setAnnualProfessionalByStatusWithAge,
  ] = useState<
    {
      age: string;
      activeCount: number;
      inactiveCount: number;
    }[]
  >([]);

  const [
    professionalByGenreWithStatus,
    setProfessionalByGenreWithStatus,
  ] = useState<
    {
      genre: string;
      activeCount: number;
      inactiveCount: number;
    }[]
  >([]);

  const [
    professionalByLocationWithStatus,
    setProfessionalByLocationWithStatus,
  ] = useState<
    {
      address_country: string;
      activeCount: number;
      inactiveCount: number;
    }[]
  >([]);

  const [
    professionalByStateWithStatus,
    setProfessionalByStateWithStatus,
  ] = useState<
    {
      address_state: string;
      activeCount: number;
      inactiveCount: number;
    }[]
  >([]);

  const queryValue = useMemo(() => {
    const q = new URLSearchParams();
    const initial = query.get('initialDate');
    const last = query.get('lastDate');

    if (initial) q.append('start', initial);
    if (last) q.append('end', last);

    return q;
  }, [query]);

  const handleGetProfessionalByPaymentStatusWithMonth = (
    data: IAnnualProfessionalByPaymentStatusWithMonth[],
  ) => {
    const APPSMpush: {
      month: number;
      monthName: string;
      nullCount: number;
      pendingCount: number;
      overdueCount: number;
      paidCount: number;
      canceledCount: number;
      year: string;
    }[] = [];

    const distinctYears: string[] = [];

    data.forEach((a) => {
      if (!distinctYears.includes(a.year)) {
        distinctYears.push(a.year);
      }
    });

    distinctYears.forEach((year) => {
      for (let i = 1; i < 13; i++) {
        const toPush = data.filter(
          (a) => Number(a.month) === i && a.year === year,
        );
        if (toPush.length !== 0) {
          const test_period =
            toPush
              .filter((a) => a.payment_status === null)
              .reduce((acc, cur) => acc + parseInt(cur.count), 0) ?? 0;

          const pending =
            toPush
              .filter((a) => a.payment_status === 'pending')
              .reduce((acc, cur) => acc + parseInt(cur.count), 0) ?? 0;

          const overdue =
            toPush
              .filter((a) => a.payment_status === 'overdue')
              .reduce((acc, cur) => acc + parseInt(cur.count), 0) ?? 0;

          const paid =
            toPush
              .filter((a) => a.payment_status === 'paid')
              .reduce((acc, cur) => acc + parseInt(cur.count), 0) ?? 0;

          const canceled =
            toPush
              .filter((a) => a.payment_status === 'canceled')
              .reduce((acc, cur) => acc + parseInt(cur.count), 0) ?? 0;

          APPSMpush.push({
            month: toPush[0].month,
            monthName: months[toPush[0].month],
            nullCount: test_period,
            pendingCount: pending,
            overdueCount: overdue,
            paidCount: paid,
            canceledCount: canceled,
            year: `${months[i]} ${year}`,
          });
        } else {
          APPSMpush.push({
            month: i,
            monthName: months[i],
            nullCount: 0,
            pendingCount: 0,
            overdueCount: 0,
            paidCount: 0,
            canceledCount: 0,
            year: `${months[i]} ${year}`,
          });
        }
      }
    });
    // console.log('APPSMpush:', APPSMpush);

    setAnnualProfessionalByPaymentStatusWithMonth(APPSMpush);
  };

  const handleHistoricProfessionalByPaymentStatus = (
    data: IHistoricAnnualProfessionalByStatus[],
  ) => {
    const HAPBSpush: {
      year: number;
      active_count: string;
      inactive_count: string;
    }[] = [];

    for (let i = 0; i < data?.length; i++) {
      const toPush = data[i];
      const check = HAPBSpush.filter((a) => a.year === toPush.year);

      HAPBSpush.push({
        year: toPush.year,
        active_count: toPush.active_number,
        inactive_count: toPush.inactive_number,
      });
      // if (check.length === 0) {
      // if (toPush.status) {
      //   HAPBSpush.push({
      //     year: toPush.year,
      //     activeCount: toPush.count,
      //     inactiveCount: 0,
      //   });
      // } else {
      //   HAPBSpush.push({
      //     year: toPush.year,
      //     activeCount: 0,
      //     inactiveCount: toPush.count,
      //   });
      // }
      // }

      // if (check.length === 1) {
      //   const aux = HAPBSpush.indexOf(check[0]);

      // if (toPush.status) {
      //   HAPBSpush[aux] = {
      //     ...check[0],
      //     activeCount: check[0].activeCount + toPush.count,
      //   };
      // } else {
      //   HAPBSpush[aux] = {
      //     ...check[0],
      //     inactiveCount: check[0].inactiveCount + toPush.count,
      //   };
      // }

      // HAPBSpush[aux] = {
      //   ...check[0],
      //   inactiveCount: check[0].inactiveCount + toPush.count,
      // };
      // }
    }

    setHistoricAnnualProfessionalByStatus(HAPBSpush);
  };

  const handleProfessionalByAccessControl = (
    data: IProfessionalByAccessControl[],
  ) => {
    const PBACaux = data.map((item) => {
      return {
        access_control: item.access_control ? 'liberado' : 'bloqueado',
        count: Number(item.count),
      };
    });

    setProfessionalByAccessControl(PBACaux);
    // *-----------------------------------------
  };

  const handleAnnualProfessionalByStatusWithMonth = (
    data: IAnnualProfessionalByStatusWithMonth[],
  ) => {
    const APSMaux = data;

    const APSMpush = [];
    for (let i = 1; i < 13; i++) {
      const toPush = APSMaux.filter((a) => Number(a.month) === i);
      if (toPush.length > 0) {
        // const active = toPush
        //   .filter((a) => a.status === true)
        //   .reduce((acc, cur) => acc + parseInt(cur.count), 0);
        // const inactive = toPush
        //   .filter((a) => a.status === false)
        //   .reduce((acc, cur) => acc + parseInt(cur.count), 0);
        // APSMpush.push({
        //   month: toPush[0].month,
        //   monthName: months[toPush[0].month],
        //   activeCount: active,
        //   inactiveCount: inactive,
        // });
        // console.log('i:', i - 1);
        // console.log('toPush:', toPush);

        const totalActiveNumbers = toPush
          .map((data) => data.active_number)
          .reduce((acc, data) => acc + data);

        const totalInactiveNumbers = toPush
          .map((data) => data.inactive_number)
          .reduce((acc, data) => acc + data);

        APSMpush.push({
          month: i,
          monthName: months[i],
          active_number: totalActiveNumbers,
          inactive_number: totalInactiveNumbers,
        });
      } else {
        APSMpush.push({
          month: i,
          monthName: months[i],
          active_number: 0,
          inactive_number: 0,
        });
      }
    }

    setAnnualProfessionalByStatusWithMonth(APSMpush);
  };

  const handleNotInformation = (message: string) => {
    return message === 'N/I' ? 'outros' : message;
  };

  const handleGetReportTrainers = async () => {
    try {
      setLoading(true);

      const response = await IBossService.getReportsProfessionals(
        queryValue.toString(),
      );

      // Annual Professional By Payment Status With Month
      const APPSMaux = response.data.annualProfessionalByPaymentStatusWithMonth;

      handleGetProfessionalByPaymentStatusWithMonth(APPSMaux);

      // *-------------------------------------------------------------------*
      // Historic Annual Professional By Status
      handleHistoricProfessionalByPaymentStatus(
        response.data.historicAnnualProfessionalByStatus,
      );

      // *-------------------------------------------------------------------*
      // Professional By Access Control
      handleProfessionalByAccessControl(
        response.data.professionalByAccessControl,
      );
      // Annual Professional By Status With Month
      handleAnnualProfessionalByStatusWithMonth(
        response.data.annualProfessionalByStatusWithMonth,
      );
      // *-------------------------------------------------------------------*

      // annual Professional By Status With Age
      const APSBAaux = response.data.annualProfessionalByStatusWithAge.map(
        (item) => {
          return {
            ...item,
            age: handleGetAgeGroup(item.age),
            count: parseInt(item.count),
          };
        },
      );

      const APSBApush: {
        age: string;
        activeCount: number;
        inactiveCount: number;
      }[] = [];

      for (let i = 0; i < APSBAaux.length; i++) {
        const toPush = APSBAaux[i];
        const check = APSBApush.filter((a) => a.age === toPush.age);
        if (check.length === 0) {
          if (toPush.status) {
            APSBApush.push({
              age: toPush.age,
              activeCount: toPush.count,
              inactiveCount: 0,
            });
          } else {
            APSBApush.push({
              age: toPush.age,
              activeCount: 0,
              inactiveCount: toPush.count,
            });
          }
        }
        if (check.length === 1) {
          const aux = APSBApush.indexOf(check[0]);
          if (toPush.status) {
            APSBApush[aux] = {
              ...check[0],
              activeCount: check[0].activeCount + toPush.count,
            };
          } else {
            APSBApush[aux] = {
              ...check[0],
              inactiveCount: check[0].inactiveCount + toPush.count,
            };
          }
        }
      }

      setAnnualProfessionalByStatusWithAge(APSBApush);
      // --------------------------------------------------------------------*

      // annual Professional By Status With Genre
      const APSBGaux = response.data.professionalByGenreWithStatus.map(
        (item) => {
          return {
            ...item,
            genre:
              item.genre === 'male'
                ? 'masculino'
                : item.genre === 'female'
                ? 'feminino'
                : item.genre === 'others'
                ? 'outros'
                : item.genre === null
                ? 'outros'
                : item.genre.toString(),
            count: parseInt(item.count),
          };
        },
      );

      const APSBGpush: {
        genre: string;
        activeCount: number;
        inactiveCount: number;
      }[] = [];

      for (let i = 0; i < APSBGaux.length; i++) {
        const toPush = APSBGaux[i];
        const check = APSBGpush.filter((a) => a.genre === toPush.genre);
        if (check.length === 0) {
          if (toPush.status) {
            APSBGpush.push({
              genre: toPush.genre,
              activeCount: toPush.count,
              inactiveCount: 0,
            });
          } else {
            APSBGpush.push({
              genre: toPush.genre,
              activeCount: 0,
              inactiveCount: toPush.count,
            });
          }
        }
        if (check.length === 1) {
          const aux = APSBGpush.indexOf(check[0]);
          if (toPush.status) {
            APSBGpush[aux] = {
              ...check[0],
              activeCount: check[0].activeCount + toPush.count,
            };
          } else {
            APSBGpush[aux] = {
              ...check[0],
              inactiveCount: check[0].inactiveCount + toPush.count,
            };
          }
        }
      }

      setProfessionalByGenreWithStatus(APSBGpush);
      // *-----------------------------------------------------------------------*

      // ? Professional By Location With Status
      const sameCountryObjects: {
        address_country: string;
        count: string;
        status: boolean;
      }[] = [];

      const apiProfessionalByLocationWithStatus =
        response.data.professionalByLocationWithStatus;

      for (let i = 0; i < apiProfessionalByLocationWithStatus.length; i++) {
        if (
          sameCountryObjects
            .map((obj) => obj.address_country)
            .includes(
              apiProfessionalByLocationWithStatus[i].address_country?.trim(),
            )
        ) {
          const repeatedObj = sameCountryObjects.find(
            (data) =>
              data.address_country?.trim() ===
              apiProfessionalByLocationWithStatus[i].address_country?.trim(),
          );

          if (repeatedObj && repeatedObj.count) {
            const sum =
              parseInt(repeatedObj.count) +
              parseInt(apiProfessionalByLocationWithStatus[i].count);
            repeatedObj.count = sum.toString();
          }
        } else {
          sameCountryObjects.push(apiProfessionalByLocationWithStatus[i]);
        }
      }

      const APSBLaux = sameCountryObjects.map((item) => {
        return {
          ...item,
          address_country:
            item.address_country === null ? 'outros' : item.address_country,
          count: parseInt(item.count),
        };
      });

      const APSBLpush: {
        address_country: string;
        activeCount: number;
        inactiveCount: number;
      }[] = [];

      for (let i = 0; i < APSBLaux.length; i++) {
        const toPush = APSBLaux[i];
        const check = APSBLpush.filter(
          (a) => a.address_country === toPush.address_country,
        );
        if (check.length === 0) {
          if (toPush.status) {
            APSBLpush.push({
              address_country: toPush.address_country,
              activeCount: toPush.count,
              inactiveCount: 0,
            });
          } else {
            APSBLpush.push({
              address_country: toPush.address_country,
              activeCount: 0,
              inactiveCount: toPush.count,
            });
          }
        }
        if (check.length === 1) {
          const aux = APSBLpush.indexOf(check[0]);
          if (toPush.status) {
            APSBLpush[aux] = {
              ...check[0],
              activeCount: check[0].activeCount + toPush.count,
            };
          } else {
            APSBLpush[aux] = {
              ...check[0],
              inactiveCount: check[0].inactiveCount + toPush.count,
            };
          }
        }
      }

      setProfessionalByLocationWithStatus(APSBLpush);
      // *-----------------------------------------------------------------------*

      // ? Professional By State With Status
      const APSBSaux = response.data.professionalByStateWithStatus.map(
        (item) => {
          return {
            ...item,
            address_state:
              item.address_state === null ? 'outros' : item.address_state,
            count: parseInt(item.count),
          };
        },
      );

      const possibleStates = [
        'RJ',
        'Rio de janeiro',
        'Rio de Janeiro',
        'Rio de janeiro ',
        'Rio de Janeiro ',
      ];

      for (let i = 0; i < APSBSaux.length; i++) {
        if (possibleStates.includes(APSBSaux[i].address_state)) {
          APSBSaux[i].address_state = 'Rio de Janeiro';
        }
      }

      const APSBSpush: {
        address_state: string;
        activeCount: number;
        inactiveCount: number;
      }[] = [];

      for (let i = 0; i < APSBSaux.length; i++) {
        const toPush = APSBSaux[i];
        const check = APSBSpush.filter(
          (a) => a.address_state === toPush.address_state,
        );
        if (check.length === 0) {
          if (toPush.status) {
            APSBSpush.push({
              address_state: toPush.address_state,
              activeCount: toPush.count,
              inactiveCount: 0,
            });
          } else {
            APSBSpush.push({
              address_state: toPush.address_state,
              activeCount: 0,
              inactiveCount: toPush.count,
            });
          }
        }
        if (check.length === 1) {
          const aux = APSBSpush.indexOf(check[0]);
          if (toPush.status) {
            APSBSpush[aux] = {
              ...check[0],
              activeCount: check[0].activeCount + toPush.count,
            };
          } else {
            APSBSpush[aux] = {
              ...check[0],
              inactiveCount: check[0].inactiveCount + toPush.count,
            };
          }
        }
      }

      setProfessionalByStateWithStatus(APSBSpush);
      setTrainerData(response.data);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  useEffect(() => {
    handleGetReportTrainers();
  }, []);

  return (
    <S.Container>
      <ExplainDate
        initial={query.get('initialDate') || ''}
        last={query.get('lastDate') || ''}
      />
      {trainersData && (
        <>
          <S.ChartContainer>
            <S.ChartTitle>Treinadores por status de assinatura</S.ChartTitle>
            <ResponsiveContainer width={750} height={250}>
              <BarChart
                data={annualProfessionalByPaymentStatusWithMonth}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip />
                <Legend />
                <ReferenceLine y={0} stroke="#000" />
                <Brush dataKey="year" height={30} stroke="#67CE67" />
                <Bar
                  dataKey="nullCount"
                  name="período de teste"
                  fill="#5a9fe0"
                />
                <Bar dataKey="paidCount" name="pago" fill="#5ae060" />
                <Bar dataKey="pendingCount" name="pendente" fill="#ecd25f" />
                <Bar dataKey="overdueCount" name="atrasado" fill="#f3ae47" />
                <Bar dataKey="canceledCount" name="cancelado" fill="#fd5a3d" />
              </BarChart>
            </ResponsiveContainer>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>Treinadores ativos e inativos por ano</S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={historicAnnualProfessionalByStatus}
              maxBarSize={20}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip
                labelStyle={{ color: '#000000' }}
                labelFormatter={(value: string) => handleNotInformation(value)}
              />
              <Legend />
              <Bar dataKey="active_count" name="ativos" fill="#5ae060" />
              <Bar dataKey="inactive_count" name="inativos" fill="#fd5a3d" />
            </BarChart>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>Treinadores por controle de acesso</S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={professionalByAccessControl}
              maxBarSize={20}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="access_control" />
              <YAxis />
              <Tooltip
                labelStyle={{ color: '#000000' }}
                itemStyle={{ color: '#000000' }}
              />
              <Legend />
              <Bar dataKey="count" name="quantidade" fill="#FFDF22" />
            </BarChart>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>Treinadores ativos e inativos</S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={annualProfessionalByStatusWithMonth}
              maxBarSize={25}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthName" />
              <YAxis />
              <Tooltip labelStyle={{ color: '#000000' }} />
              <Legend />
              <Bar dataKey="active_number" name="ativos" fill="#5ae060" />
              <Bar dataKey="inactive_number" name="inativos" fill="#fd5a3d" />
            </BarChart>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>
              Treinadores ativos e inativos pela idade
            </S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={annualProfessionalByStatusWithAge}
              maxBarSize={20}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="age" />
              <YAxis />
              <Tooltip
                labelStyle={{ color: '#000000' }}
                labelFormatter={(value: string) =>
                  value === 'N/I' ? 'outros' : `${value} anos`
                }
              />
              <Legend />
              <Bar dataKey="activeCount" name="ativos" fill="#5ae060" />
              <Bar dataKey="inactiveCount" name="inativos" fill="#fd5a3d" />
            </BarChart>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>
              Treinadores ativos e inativos pelo gênero
            </S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={professionalByGenreWithStatus}
              maxBarSize={25}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="genre" />
              <YAxis />
              <Tooltip labelStyle={{ color: '#000000' }} />
              <Legend />
              <Bar dataKey="activeCount" name="ativos" fill="#5ae060" />
              <Bar dataKey="inactiveCount" name="inativos" fill="#fd5a3d" />
            </BarChart>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>Treinadores ativos e inativos por país</S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={professionalByLocationWithStatus}
              maxBarSize={25}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="address_country" />
              <YAxis />
              <Tooltip
                labelStyle={{ color: '#000000' }}
                labelFormatter={(value: string) => handleNotInformation(value)}
              />
              <Legend />
              <Bar dataKey="activeCount" name="ativos" fill="#5ae060" />
              <Bar dataKey="inactiveCount" name="inativos" fill="#fd5a3d" />
            </BarChart>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>
              Treinadores ativos e inativos por estado
            </S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={professionalByStateWithStatus}
              maxBarSize={25}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="address_state" />
              <YAxis />
              <Tooltip
                labelStyle={{ color: '#000000' }}
                labelFormatter={(value: string) => handleNotInformation(value)}
              />
              <Legend />
              <Bar dataKey="activeCount" name="ativos" fill="#5ae060" />
              <Bar dataKey="inactiveCount" name="inativos" fill="#fd5a3d" />
            </BarChart>
          </S.ChartContainer>
        </>
      )}
    </S.Container>
  );
}

export default TrainersCharts;
